import React from 'react';
import PropTypes from 'prop-types';

import {Button, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography} from 'views/components';
import {Close, ExpandMore} from 'views/icons';
import {Translate} from 'views/module';

const RoutinesEdit = (props) => {
  const {open, routine, onClose, onSave} = props;

  const SCHEDULE_TYPES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM', 'ONE_TIME'];
  const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [scheduleType, setScheduleType] = React.useState('DAILY');
  const [time, setTime] = React.useState('');
  const [dayOfWeek, setDayOfWeek] = React.useState('MON');
  const [dayOfMonth, setDayOfMonth] = React.useState('1');
  const [minute, setMinute] = React.useState('0');
  const [expression, setExpression] = React.useState('');
  const [datetime, setDatetime] = React.useState('');

  React.useEffect(() => {
    if (!routine) return;
    const s = routine.schedule || {};
    setTitle(routine.title || '');
    setDescription(routine.description || '');
    setInstructions(routine.instructions || '');
    setScheduleType((s.type || 'DAILY').toUpperCase());
    setTime(s.time || '');
    setDayOfWeek(s.dayOfWeek || 'MON');
    setDayOfMonth(String(s.dayOfMonth || 1));
    setMinute(String(s.minute ?? 0));
    setExpression(s.expression || '');
    if (s.datetime) {
      const d = new Date(s.datetime + 'Z');
      const offset = d.getTimezoneOffset() * 60000;
      setDatetime(new Date(d - offset).toISOString().slice(0, 16));
    } else {
      setDatetime('');
    }
  }, [routine]);

  const buildSchedule = () => {
    if (scheduleType === 'ONE_TIME') {
      return {type: 'ONE_TIME', datetime: new Date(datetime).toISOString().slice(0, 19)};
    }
    if (scheduleType === 'CUSTOM') return {type: scheduleType, expression};
    if (scheduleType === 'HOURLY') {
      const s = {type: scheduleType};
      const m = parseInt(minute, 10);
      if (!isNaN(m) && m > 0) s.minute = m;
      return s;
    }
    const s = {type: scheduleType, time};
    if (scheduleType === 'WEEKLY') s.dayOfWeek = dayOfWeek;
    if (scheduleType === 'MONTHLY') s.dayOfMonth = parseInt(dayOfMonth, 10);
    return s;
  };

  const needsTime = scheduleType === 'DAILY' || scheduleType === 'WEEKLY' || scheduleType === 'MONTHLY';
  const isValid = title.trim() && instructions.trim() &&
    (scheduleType === 'ONE_TIME' ? datetime.trim() :
      scheduleType === 'CUSTOM' ? expression.trim() :
        needsTime ? time.trim() : true);

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      title: title.trim(),
      description: description.trim(),
      instructions: instructions.trim(),
      schedule: buildSchedule(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{px: '28px', pt: '24px', pb: '0'}}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: 400,
              color: '#1A1916',
              textDecoration: 'underline',
              textDecorationColor: '#DA7955',
              textUnderlineOffset: '5px',
            }}
          >
            <Translate expression='routines.Edit.Title' />
          </Typography>
          <IconButton onClick={onClose} size='small' sx={{color: '#A8A49E'}}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{px: '28px', pt: '20px !important', pb: '28px'}}>
        <Stack sx={{gap: '18px'}}>
          <Stack sx={{gap: '8px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.Edit.TitleLabel' />
            </Typography>
            <TextField
              value={title}
              onChange={setTitle}
              placeholder='Routine title'
              fullWidth
              sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
            />
          </Stack>

          <Stack sx={{gap: '8px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.Edit.DescriptionLabel' />
            </Typography>
            <TextField
              value={description}
              onChange={setDescription}
              placeholder='Optional description'
              fullWidth
              sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
            />
          </Stack>

          <Stack sx={{gap: '8px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.Edit.InstructionsLabel' />
            </Typography>
            <TextField
              value={instructions}
              onChange={setInstructions}
              multiline
              minRows={4}
              fullWidth
              sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
            />
          </Stack>

          <Stack sx={{gap: '8px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.Edit.ScheduleType' />
            </Typography>
            <TextField
              select
              value={scheduleType}
              onChange={setScheduleType}
              fullWidth
              sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                '& .MuiSelect-select': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'},
                '& .MuiSelect-icon': {color: '#6B6760', right: '10px'}}}
              SelectProps={{IconComponent: ExpandMore}}
            >
              {SCHEDULE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t.charAt(0) + t.slice(1).toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {scheduleType === 'HOURLY' && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.MinuteLabel' />
              </Typography>
              <TextField
                value={minute}
                onChange={setMinute}
                type='number'
                placeholder='0'
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
              />
            </Stack>
          )}

          {needsTime && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.TimeLabel' />
              </Typography>
              <TextField
                value={time}
                onChange={setTime}
                placeholder='09:00'
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
              />
            </Stack>
          )}

          {scheduleType === 'WEEKLY' && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.DayOfWeekLabel' />
              </Typography>
              <TextField
                select
                value={dayOfWeek}
                onChange={setDayOfWeek}
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiSelect-select': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'},
                  '& .MuiSelect-icon': {color: '#6B6760', right: '10px'}}}
                SelectProps={{IconComponent: ExpandMore}}
              >
                {DAYS_OF_WEEK.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Stack>
          )}

          {scheduleType === 'MONTHLY' && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.DayOfMonthLabel' />
              </Typography>
              <TextField
                value={dayOfMonth}
                onChange={setDayOfMonth}
                type='number'
                placeholder='1'
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
              />
            </Stack>
          )}

          {scheduleType === 'CUSTOM' && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.ExpressionLabel' />
              </Typography>
              <TextField
                value={expression}
                onChange={setExpression}
                placeholder='rate(5 minutes)'
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
              />
              <Typography sx={{fontSize: '11px', color: '#A8A49E', lineHeight: '160%'}}>
                <Translate expression='routines.Edit.ExpressionHint' />
              </Typography>
            </Stack>
          )}

          {scheduleType === 'ONE_TIME' && (
            <Stack sx={{gap: '8px'}}>
              <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
                <Translate expression='routines.Edit.DateTimeLabel' />
              </Typography>
              <TextField
                value={datetime}
                onChange={setDatetime}
                type='datetime-local'
                fullWidth
                sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  '& .MuiOutlinedInput-input': {px: '12px', py: '10px', fontSize: '13px', color: '#1A1916'}}}
              />
            </Stack>
          )}

          <Stack direction='row' justifyContent='flex-end' sx={{gap: '8px', pt: '8px'}}>
            <Button
              onClick={onClose}
              variant='outlined'
              disableElevation
              sx={{
                'borderRadius': '7px',
                'px': '14px',
                'py': '8px',
                'fontWeight': 500,
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#8E8A84',
                'borderColor': '#E8E4DE',
                'backgroundColor': '#FFFFFF',
                '&:hover': {backgroundColor: '#FFFFFF', borderColor: '#E8E4DE', boxShadow: 'none'},
              }}
            >
              <Translate expression='routines.Edit.Cancel' />
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid}
              disableElevation
              sx={{
                'borderRadius': '7px',
                'px': '14px',
                'py': '8px',
                'fontWeight': 500,
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#FFFFFF',
                'backgroundColor': '#DA7955',
                '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'},
                '&.Mui-disabled': {backgroundColor: '#E8E4DE', color: '#A8A49E'},
              }}
            >
              <Translate expression='routines.Edit.Save' />
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

RoutinesEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  routine: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default React.memo(RoutinesEdit);
